import { supabase } from './supabase';
import type { Tenant, TenantUser, TenantInvite, TenantStats } from '../types/tenant';

let currentTenant: Tenant | null = null;

export async function getCurrentTenant(): Promise<Tenant | null> {
  if (currentTenant) return currentTenant;

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  // Get tenant user record
  const { data: tenantUser, error: tenantUserError } = await supabase
    .from('tenant_users')
    .select('tenant_id')
    .eq('user_id', user.id)
    .maybeSingle(); // Use maybeSingle instead of single to handle no rows gracefully

  if (tenantUserError) return null;
  if (!tenantUser) return null;

  // Get tenant details
  const { data: tenant, error: tenantError } = await supabase
    .from('tenants')
    .select('*')
    .eq('id', tenantUser.tenant_id)
    .single();

  if (tenantError) return null;

  currentTenant = tenant;
  return tenant;
}

export function setCurrentTenant(tenant: Tenant | null) {
  currentTenant = tenant;
}

export async function getTenantUsers(tenantId: string): Promise<TenantUser[]> {
  const { data, error } = await supabase
    .from('tenant_users')
    .select(`
      *,
      user:users (
        email,
        user_metadata
      )
    `)
    .eq('tenant_id', tenantId);

  if (error) throw error;
  return data || [];
}

export async function inviteUser(tenantId: string, email: string, role: 'admin' | 'member'): Promise<TenantInvite> {
  const { data, error } = await supabase
    .from('tenant_invites')
    .insert([{
      tenant_id: tenantId,
      email,
      role,
      token: crypto.randomUUID(),
      expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() // 7 days
    }])
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function getTenantStats(tenantId: string): Promise<TenantStats> {
  const [
    { count: totalUsers },
    { count: totalProperties },
    { count: totalUnits },
    { count: totalTenants },
    { data: storageData }
  ] = await Promise.all([
    supabase.from('tenant_users').select('*', { count: 'exact' }).eq('tenant_id', tenantId),
    supabase.from('properties').select('*', { count: 'exact' }).eq('tenant_id', tenantId),
    supabase.from('units').select('*', { count: 'exact' }).eq('tenant_id', tenantId),
    supabase.from('tenants').select('*', { count: 'exact' }).eq('tenant_id', tenantId),
    supabase.rpc('calculate_tenant_storage', { p_tenant_id: tenantId })
  ]);

  const { data: activity } = await supabase
    .from('audit_logs')
    .select('created_at')
    .eq('tenant_id', tenantId)
    .order('created_at', { ascending: false })
    .limit(1)
    .single();

  return {
    total_users: totalUsers || 0,
    total_properties: totalProperties || 0,
    total_units: totalUnits || 0,
    total_tenants: totalTenants || 0,
    storage_used: storageData?.size || 0,
    last_activity: activity?.created_at || new Date().toISOString()
  };
}